import React from 'react';
import moment from 'moment-timezone';
import { IShow, IShowCapacityMetrics } from '@eagerdog/interfaces';
import { Constants } from '@eagerdog/constants';
import ProgressBar from '../ProgressBar/ProgressBar';
import styles from './ShowCapacityRow.module.scss';

/** Extended metrics with waitlist_count (added in backend, may not be in published interface yet) */
type ShowCapacityMetricsWithWaitlist = IShowCapacityMetrics & { waitlist_count?: number };

const HERDING_SHOW_TYPES = [
  Constants.show_type.herding,
  Constants.show_type.pre_trial_herding,
  Constants.show_type.instinct_test_herding,
];

interface IShowCapacityRowProps {
  show: IShow;
  metrics?: IShowCapacityMetrics;
  onSetLimit?: () => void; // Callback to open manage show modal to waitlist tab
  onViewWaitlist?: () => void; // Callback to open manage show modal to waitlist tab (View Waitlist link)
  eventTimezone?: string;
}

const ShowCapacityRow: React.FC<IShowCapacityRowProps> = ({ show, metrics, onSetLimit, onViewWaitlist, eventTimezone }) => {
  const isPerLevelLimit = show.waitlist_option === Constants.waitlist_options.per_level_limit;
  const isHerding = HERDING_SHOW_TYPES.includes(show.show_type);

  // For herding, deduplicate by section so each stock type is only counted once
  const perLevelLimitTotal = (show.show_elements || []).reduce((sum, se) => {
    const sAny = se as any;
    if (isHerding) {
      // Only count each unique section once — skip duplicate course/level combos
      return sum; // handled separately below
    }
    const maxEntries = Number(sAny.max_entries || 0);
    return sum + (Number.isFinite(maxEntries) ? maxEntries : 0);
  }, 0);

  // For herding per_level_limit: sum one max_entries per unique section
  const herdingStockTotal = (() => {
    if (!isHerding || !isPerLevelLimit) return 0;
    const seenSections = new Set<string>();
    let total = 0;
    for (const se of ((show.show_elements || []) as any[])) {
      if (se.section && se.max_entries != null && !seenSections.has(se.section)) {
        seenSections.add(se.section);
        total += Number(se.max_entries) || 0;
      }
    }
    return total;
  })();

  const effectiveTotal = isHerding && isPerLevelLimit ? herdingStockTotal : perLevelLimitTotal;

  // Check if waitlist is properly configured with a limit
  const hasWaitlistOption = show.waitlist_option && show.waitlist_option !== Constants.waitlist_options.none;
  const hasLimit = isPerLevelLimit
    ? effectiveTotal > 0
    : !!(show.waitlist_limit && show.waitlist_limit > 0);
  const isFullyConfigured = hasWaitlistOption && hasLimit;

  const handleSetLimitClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const scrollY = window.scrollY || window.pageYOffset;
    sessionStorage.setItem('eventPageScrollPosition', scrollY.toString());
    onSetLimit!();
  };

  // If not fully configured, show empty state
  if (!isFullyConfigured) {
    return (
      <div className={styles.emptyState}>
        <span className={styles.emptyStateText}>A limit has not been set for this show.</span>
        {onSetLimit && (
          <button 
            type="button"
            className={styles.setLimitButton} 
            onClick={handleSetLimitClick}
          >
            Set Limit
          </button>
        )}
      </div>
    );
  }

  // Don't render progress bar if metrics not loaded yet
  if (!metrics) {
    return null;
  }

  const limit = isPerLevelLimit ? effectiveTotal : (show.waitlist_limit || 0);
  const waitlistCount = (metrics as ShowCapacityMetricsWithWaitlist).waitlist_count ?? 0;
  const hasWaitlistEnabled = !!show.waitlist_enabled;
  const showViewWaitlist = !!onViewWaitlist;

  const handleViewWaitlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const scrollY = window.scrollY || window.pageYOffset;
    sessionStorage.setItem('eventPageScrollPosition', scrollY.toString());
    onViewWaitlist!();
  };

  const waitlistNode = showViewWaitlist ? (
    hasWaitlistEnabled ? (
      <button type="button" className={styles.viewWaitlistLink} onClick={handleViewWaitlistClick}>
        View Waitlist ({waitlistCount} {waitlistCount === 1 ? 'entry' : 'entries'})
      </button>
    ) : (
      <span className={styles.noWaitlistText}>No waitlist</span>
    )
  ) : null;

  // ── Per-judge bars (only for judge-time limit types with 2+ judges) ─────────
  const judgeCapacities = metrics.judge_capacities;
  const isJudgeTimeLimit =
    show.waitlist_option === Constants.waitlist_options.daily_judge_time_limit ||
    show.waitlist_option === Constants.waitlist_options.daily_trial_time_limit;
  const isMrvLimit = show.waitlist_option === Constants.waitlist_options.daily_judge_mrv_limit;

  if ((isJudgeTimeLimit || isMrvLimit) && judgeCapacities && judgeCapacities.length > 1) {
    const isPerJudgeLimit = show.waitlist_option === Constants.waitlist_options.daily_judge_time_limit;

    return (
      <div className={styles.multiJudgeRow}>
        <div className={styles.judgeCapacityList}>
          {judgeCapacities.map(jc => {
            let current = 0;
            let label = '';

            switch (show.waitlist_option) {
              case Constants.waitlist_options.daily_judge_mrv_limit: {
                const mrv = jc.current_mrv ?? 0;
                current = mrv;
                label = `${mrv.toFixed(1)} / ${limit} MRV`;
                break;
              }
              case Constants.waitlist_options.daily_judge_time_limit:
              case Constants.waitlist_options.daily_trial_time_limit:
                current = jc.current_minutes;
                label = `${jc.current_minutes.toFixed(1)} / ${isPerJudgeLimit ? limit : ''} min`.trim();
                if (!isPerJudgeLimit) label = `${jc.current_minutes.toFixed(1)} min`;
                break;
              default:
                current = jc.current_minutes;
                label = `${jc.current_minutes.toFixed(1)} min`;
            }

            const pct = limit > 0 ? Math.min(100, (current / limit) * 100) : 0;
            return (
              <div key={jc.judge_name} className={styles.judgeCapacityItem}>
                <span className={styles.judgeNameLabel}>{jc.judge_name}</span>
                <div className={styles.judgeProgressSection}>
                  <ProgressBar percentage={pct} />
                </div>
                <span className={styles.judgeMinutesLabel}>{label}</span>
              </div>
            );
          })}
        </div>
        {waitlistNode && <div className={styles.judgeListWaitlist}>{waitlistNode}</div>}
      </div>
    );
  }

  // ── Per-judge entry bars (entries_per_judge, shown even with one judge) ────
  if (show.waitlist_option === Constants.waitlist_options.entries_per_judge && judgeCapacities) {
    const releaseLabel = metrics.manual_entry_limit_date
      ? moment(metrics.manual_entry_limit_date).tz(eventTimezone || moment.tz.guess()).format('MMM D, h:mm A z')
      : '';

    return (
      <div className={styles.multiJudgeRow}>
        <div className={styles.judgeCapacityList}>
          {judgeCapacities.map(jc => {
            const entries = (jc.online_entries ?? 0) + (jc.manual_entries ?? 0);
            const total = jc.total_limit ?? 0;
            const isOver = total > 0 && entries > total;
            const name = jc.judge_name
              ? `${jc.judge_name}${jc.judge_number ? ` (#${jc.judge_number})` : ''}`
              : 'No judge assigned';
            const isNoJudgeBucket = !jc.judge_name || jc.judge_name === 'No judge assigned';
            const canSetLimit = !!onSetLimit && !!jc.limit_missing && !jc.removed_judge && !isNoJudgeBucket;
            const reserve = jc.manual_reserve ?? 0;

            let subLine = '';
            if (jc.removed_judge) {
              subLine = 'Judge no longer in this trial';
            } else if (jc.limit_missing) {
              subLine = 'Limit required';
            } else if (reserve > 0) {
              subLine = jc.reserve_released
                ? `Online ${jc.online_entries ?? 0} · Mail-in ${jc.manual_entries ?? 0} · Mail-in hold released`
                : `Online ${jc.online_entries ?? 0} / ${jc.online_limit ?? 0} · Mail-in ${jc.manual_entries ?? 0} / ${reserve} held${releaseLabel ? ` until ${releaseLabel}` : ''}`;
            }

            return (
              <div key={`${jc.judge_name}|${jc.judge_number || ''}|${jc.removed_judge ? 'removed' : ''}`} className={styles.judgeEntryItem}>
                <div className={styles.judgeCapacityItem}>
                  <span className={styles.judgeNameLabel}>{name}</span>
                  <div className={styles.judgeProgressSection}>
                    <ProgressBar percentage={total > 0 ? (entries / total) * 100 : 0} color={isOver ? '#c0392b' : undefined} />
                  </div>
                  <span className={isOver ? `${styles.judgeMinutesLabel} ${styles.overLimit}` : styles.judgeMinutesLabel}>
                    {total > 0 ? `${entries} / ${total} entries` : `${entries} ${entries === 1 ? 'entry' : 'entries'}`}
                  </span>
                </div>
                {subLine && (
                  <div className={styles.judgeEntrySubLineRow}>
                    <span className={jc.limit_missing || jc.removed_judge ? `${styles.judgeEntrySubLine} ${styles.warning}` : styles.judgeEntrySubLine}>
                      {subLine}
                    </span>
                    {canSetLimit && (
                      <button type="button" className={`${styles.setLimitButton} ${styles.small}`} onClick={handleSetLimitClick}>
                        Set Limit
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {metrics.detective_capacity && (() => {
            const dc = metrics.detective_capacity;
            const entries = dc.online_entries + dc.manual_entries;
            const isOver = dc.total_limit > 0 && entries > dc.total_limit;
            const subLine = dc.manual_reserve > 0
              ? dc.reserve_released
                ? `Online ${dc.online_entries} · Mail-in ${dc.manual_entries} · Mail-in hold released`
                : `Online ${dc.online_entries} / ${dc.online_limit} · Mail-in ${dc.manual_entries} / ${dc.manual_reserve} held${releaseLabel ? ` until ${releaseLabel}` : ''}`
              : '';
            return (
              <div className={styles.judgeEntryItem}>
                <div className={styles.judgeCapacityItem}>
                  <span className={styles.judgeNameLabel}>Detective (all judges)</span>
                  <div className={styles.judgeProgressSection}>
                    <ProgressBar percentage={dc.total_limit > 0 ? (entries / dc.total_limit) * 100 : 0} color={isOver ? '#c0392b' : undefined} />
                  </div>
                  <span className={isOver ? `${styles.judgeMinutesLabel} ${styles.overLimit}` : styles.judgeMinutesLabel}>
                    {`${entries} / ${dc.total_limit} entries`}
                  </span>
                </div>
                {subLine && (
                  <div className={styles.judgeEntrySubLineRow}>
                    <span className={styles.judgeEntrySubLine}>{subLine}</span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        {waitlistNode && <div className={styles.judgeListWaitlist}>{waitlistNode}</div>}
      </div>
    );
  }

  // ── Per-stock bars (herding per_level_limit) ──────────────────────────────
  const stockCapacities = metrics.stock_capacities;
  if (isHerding && isPerLevelLimit && stockCapacities && stockCapacities.length > 0) {
    // Build a map of max_entries per section from show_elements
    const maxBySection = new Map<string, { max: number; waitlistEnabled: boolean; waitlistSize: number }>();
    for (const se of ((show.show_elements || []) as any[])) {
      if (se.section && se.max_entries != null && !maxBySection.has(se.section)) {
        maxBySection.set(se.section, {
          max: Number(se.max_entries) || 0,
          waitlistEnabled: !!se.waitlist_enabled,
          waitlistSize: Number(se.waitlist_size) || 0,
        });
      }
    }

    return (
      <div className={styles.multiJudgeRow}>
        <div className={styles.judgeCapacityList}>
          {stockCapacities.map(sc => {
            const sectionConfig = maxBySection.get(sc.section);
            const sectionMax = sectionConfig?.max ?? 0;
            const pct = sectionMax > 0 ? Math.min(100, (sc.current_entries / sectionMax) * 100) : 0;
            const available = Math.max(0, sectionMax - sc.current_entries);
            const label = `${sc.current_entries} / ${sectionMax} entries`;

            return (
              <div key={sc.section} className={styles.judgeCapacityItem}>
                <span className={styles.judgeNameLabel}>{sc.section}</span>
                <div className={styles.judgeProgressSection}>
                  <ProgressBar percentage={pct} />
                </div>
                <span className={styles.judgeMinutesLabel}>{label}</span>
                {sectionConfig?.waitlistEnabled && sectionConfig.waitlistSize > 0 && sc.waitlist_count > 0 && (
                  <span className={styles.stockWaitlistBadge}>
                    WL: {sc.waitlist_count}/{sectionConfig.waitlistSize}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {waitlistNode && <div className={styles.judgeListWaitlist}>{waitlistNode}</div>}
      </div>
    );
  }

  // ── Single-bar layout (all other cases) ──────────────────────────────────
  const getProgressPercentage = (): number => {
    if (limit === 0) return 0;
    let current = 0;
    switch (show.waitlist_option) {
      case Constants.waitlist_options.show_entry_limit:
        current = metrics.current_entries;
        break;
      case Constants.waitlist_options.daily_judge_time_limit:
      case Constants.waitlist_options.daily_trial_time_limit:
        current = metrics.current_minutes;
        break;
      case Constants.waitlist_options.daily_judge_mrv_limit:
        current = metrics.current_mrv ?? 0;
        break;
      case Constants.waitlist_options.show_dog_limit:
        current = metrics.current_dogs;
        break;
      case Constants.waitlist_options.per_level_limit:
        current = metrics.current_entries;
        break;
      default:
        return 0;
    }
    return (current / limit) * 100;
  };

  const getCapacityLabel = (): string => {
    switch (show.waitlist_option) {
      case Constants.waitlist_options.show_entry_limit:
        return `${metrics.current_entries} / ${limit} entries`;
      case Constants.waitlist_options.daily_judge_time_limit: {
        const daily = `${metrics.current_minutes.toFixed(1)} / ${limit} minutes`;
        if (metrics.this_show_minutes != null && metrics.this_show_minutes !== metrics.current_minutes) {
          return `${metrics.this_show_minutes.toFixed(1)} this trial · ${daily} (daily total)`;
        }
        return daily;
      }
      case Constants.waitlist_options.daily_trial_time_limit:
        return `${metrics.current_minutes.toFixed(1)} / ${limit} minutes`;
      case Constants.waitlist_options.daily_judge_mrv_limit:
        return `${(metrics.current_mrv ?? 0).toFixed(1)} / ${limit} MRV`;
      case Constants.waitlist_options.show_dog_limit:
        return `${metrics.current_dogs} / ${limit} dogs`;
      case Constants.waitlist_options.per_level_limit:
        return `${metrics.current_entries} / ${limit} entries (per-level total)`;
      default:
        return '';
    }
  };

  const progressPercentage = getProgressPercentage();

  return (
    <div className={styles.showCapacityRow}>
      <div className={styles.metricsSection}>
        <span className={styles.capacityLabel}>{getCapacityLabel()}</span>
        {showViewWaitlist && (
          <div className={styles.viewWaitlistMobile}>{waitlistNode}</div>
        )}
      </div>
      <div className={styles.progressSection}>
        <ProgressBar percentage={progressPercentage} />
      </div>
      {showViewWaitlist && (
        <div className={styles.viewWaitlistDesktop}>{waitlistNode}</div>
      )}
    </div>
  );
};

export default ShowCapacityRow;

